/**
 * Installed-apps model for the DopeOS desktop (ADR-0020).
 *
 * The first desktop holds only CHAT and TERMINAL. Every other app arrives as a
 * story beat ("VINNIE SENT YOU MARKET.APP") and leaves a desktop notification.
 *
 * Source of truth:
 *   1. `core.installedApps` from core state (V2-L8a-3 exposes it on
 *      GET /core/state). The `install_app` story beats are the only way apps
 *      arrive; `firstSession.js` starts the story on the first Chat open and
 *      syncs this list.
 *   2. A local per-player set, used only for the offline fallback install
 *      (`installApp('market')` when `/story/start` fails) and the reserved
 *      `app.installed` core event. It is unioned with (1), never overrides it.
 *
 * QA / dev: VITE_QA_E2E=true or `?apps=all` installs everything so the
 * existing QA flows keep working; `?apps=default` forces the new-player
 * desktop even in QA builds.
 */
import useGameState from './gameState';

export const APP_CATALOG = [
  { id: 'chat', label: 'CHAT', glyph: 'CH', file: 'chat.app', view: 'comms_window' },
  { id: 'terminal', label: 'TERMINAL', glyph: '>_', file: 'terminal.app', view: 'terminal_window' },
  { id: 'market', label: 'MARKET', glyph: 'MK', file: 'market.app', view: 'market' },
  { id: 'route', label: 'TICKETS', glyph: 'TK', file: 'tickets.app', view: 'tickets' },
  { id: 'ledger', label: 'LEDGER', glyph: 'LG', file: 'ledger.app', view: 'ledger' },
  { id: 'coat', label: 'COAT', glyph: 'CT', file: 'coat.app', view: 'coat' },
  { id: 'journal', label: 'EXPEDIENTE', glyph: 'EX', file: 'expediente.app', view: 'journal' },
  { id: 'evidence', label: 'EVIDENCE', glyph: 'EV', file: 'evidence.app', view: 'evidence' },
  { id: 'story', label: 'STORY', glyph: 'SC', file: 'story.app', view: 'story' },
  { id: 'apps', label: 'APPS', glyph: 'AP', file: 'apps.app', view: 'apps' },
];

export const DEFAULT_APPS = ['chat', 'terminal'];
const STORAGE_PREFIX = 'dcw_installed_apps:';
const NOTIFICATION_TTL_MS = 9000;

const KNOWN = new Set(APP_CATALOG.map((a) => a.id));
const listeners = new Set();
let notifications = [];
let seq = 0;
const unseen = new Set();
const badges = new Map();
const announced = new Set();

export function getApp(appId) {
  return APP_CATALOG.find((a) => a.id === appId) || null;
}

export function isQaAllApps() {
  let param = null;
  try {
    if (typeof window !== 'undefined') {
      param = new URLSearchParams(window.location.search).get('apps');
    }
  } catch {
    /* URL unavailable */
  }
  if (param === 'default') return false; // lets QA builds exercise the new-player desktop
  if (param === 'all') return true;
  try {
    return import.meta.env?.VITE_QA_E2E === 'true';
  } catch {
    return false;
  }
}

function currentPlayerKey() {
  const core = useGameState.getState().core || {};
  return core.playerId || 'guest';
}

const cache = new Map();

function readClient(playerKey) {
  if (cache.has(playerKey)) return cache.get(playerKey);
  const ids = readClientStorage(playerKey);
  cache.set(playerKey, ids);
  return ids;
}

function readClientStorage(playerKey) {
  try {
    const raw = window.localStorage.getItem(STORAGE_PREFIX + playerKey);
    const parsed = raw ? JSON.parse(raw) : null;
    if (Array.isArray(parsed)) return parsed.filter((id) => KNOWN.has(id));
  } catch {
    /* storage unavailable or corrupt */
  }
  return null;
}

function writeClient(playerKey, ids) {
  cache.set(playerKey, ids);
  try {
    window.localStorage.setItem(STORAGE_PREFIX + playerKey, JSON.stringify(ids));
  } catch {
    /* storage unavailable */
  }
}

/** Ordered by catalog; chat + terminal are always present. */
export function getInstalledApps() {
  if (isQaAllApps()) return APP_CATALOG.map((a) => a.id);
  const core = useGameState.getState().core || {};
  const fromCore = Array.isArray(core.installedApps) ? core.installedApps : [];
  const fromClient = readClient(currentPlayerKey()) || [];
  const ids = new Set(DEFAULT_APPS);
  // Union, not override: the offline fallback install must stay visible next
  // to the server-authoritative list.
  [...fromCore, ...fromClient].forEach((id) => KNOWN.has(id) && ids.add(id));
  return APP_CATALOG.filter((a) => ids.has(a.id)).map((a) => a.id);
}

export function isInstalled(appId) {
  return getInstalledApps().includes(appId);
}

function emit() {
  listeners.forEach((fn) => {
    try { fn(); } catch { /* listener errors never break the shell */ }
  });
}

export function subscribe(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/**
 * Install an app and raise the desktop notification.
 * Idempotent: returns false when the app is unknown or already installed.
 */
export function installApp(appId, { from = null } = {}) {
  const app = getApp(appId);
  if (!app || isInstalled(appId)) return false;
  const key = currentPlayerKey();
  const next = Array.from(new Set([...(readClient(key) || []), appId]));
  writeClient(key, next);
  announced.add(appId);
  unseen.add(appId);
  const sender = from ? String(from).toUpperCase() : 'SYSTEM';
  notifications.push({
    id: ++seq,
    appId,
    from: sender,
    text: `${sender} SENT YOU ${app.file.toUpperCase()}`,
    at: Date.now(),
  });
  emit();
  return true;
}

/**
 * Raise the desktop notification for an app the server already installed.
 *
 * ADR-0020 requires every install to leave a notification, including the ones
 * that arrive through `GET /core/state` after a story choice. `installApp`
 * cannot do this alone because it is idempotent and skips installed apps.
 * Announced once per app per page session so repeated state syncs stay quiet.
 */
export function announceApp(appId, { from = null } = {}) {
  const app = getApp(appId);
  if (!app || announced.has(appId)) return false;
  announced.add(appId);
  unseen.add(appId);
  const sender = from ? String(from).toUpperCase() : 'SYSTEM';
  notifications.push({
    id: ++seq,
    appId,
    from: sender,
    text: `${sender} SENT YOU ${app.file.toUpperCase()}`,
    at: Date.now(),
  });
  emit();
  return true;
}

/**
 * Announce every app that appeared since `previousIds` (chat and terminal are
 * part of the first desktop and never announce). Returns the announced ids.
 */
export function announceNewInstalls(previousIds, { from = null } = {}) {
  const before = new Set(previousIds || []);
  const added = [];
  getInstalledApps().forEach((id) => {
    if (before.has(id) || id === 'chat' || id === 'terminal') return;
    if (announceApp(id, { from })) added.push(id);
  });
  return added;
}

/** Notifications still inside their display window (newest last). */
export function getActiveNotifications(now = Date.now()) {
  return notifications.filter((n) => now - n.at < NOTIFICATION_TTL_MS);
}

export function dismissNotification(id) {
  notifications = notifications.filter((n) => n.id !== id);
  emit();
}

export function isNewApp(appId) {
  return unseen.has(appId);
}

export function markAppSeen(appId) {
  if (unseen.delete(appId)) emit();
}

/** Unread count shown on an app's desktop icon (0 = no badge). */
export function getBadge(appId) {
  return badges.get(appId) || 0;
}

export function setBadge(appId, count) {
  const next = Math.max(0, Math.floor(Number(count) || 0));
  if ((badges.get(appId) || 0) === next) return;
  if (next) badges.set(appId, next);
  else badges.delete(appId);
  emit();
}

/**
 * Core event shape (reserved for V2-L8a / the `install_app` story effect):
 *   { type: 'app.installed', app: 'market', from: 'vinnie' }
 */
export function applyCoreEvent(event) {
  if (!event || event.type !== 'app.installed') return false;
  return installApp(event.app || event.appId, { from: event.from });
}

if (typeof window !== 'undefined') {
  window.addEventListener('dcw:core-event', (e) => applyCoreEvent(e.detail));
}

export function __resetForTests() {
  notifications = [];
  unseen.clear();
  badges.clear();
  announced.clear();
  listeners.clear();
  cache.clear();
}
